import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MailingConfig } from './types/mailing-config.type';

/**
 * Upserts (creates or updates) a mailing configuration for a given mailing ID.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The unique identifier of the mailing to upsert the configuration for.
 * @param data - The mailing configuration data to be upserted.
 * @returns A promise that resolves to the operation result of the upsert action.
 * @throws Will throw an error if `mailingId` or `data` is not defined, or if the `mailingId` in `data` does not
 * match the provided `mailingId`.
 */
export const upsertMailingConfig = async (instance: KyInstance, mailingId?: number, data?: MailingConfig) => {
  try {
    if (isNotDefined(data) || isNotDefined(mailingId)) {
      throw new Error('MailingId and config are required.');
    }

    if (data.mailingId !== mailingId) {
      throw new Error('MailingId in config must match the provided mailingId.');
    }

    return await instance
      .post<OperationResult>(formatString(Uri.mailing.upsertMailingConfig, mailingId.toString()), {
        json: data,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
